// CombinationTeamAtLeastOneFromEachTask.js
// Kombinacje bez powtórzeń — wybór k-osobowej drużyny z co najmniej 1 kobietą i 1 mężczyzną.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// stabilne nCk
function nCk(n, k) {
  if (k < 0 || k > n) return 0;
  k = Math.min(k, n - k);
  let num = 1, den = 1;
  for (let i = 1; i <= k; i++) { num *= (n - k + i); den *= i; }
  return Math.floor(num / den);
}

export function generateTask() {
  // losujemy tak, by zadanie było sensowne i nie za duże
  const w = Math.floor(Math.random() * 4) + 3;   // 3..6 kobiet
  const m = Math.floor(Math.random() * 6) + 5;   // 5..10 mężczyzn
  const n = w + m;

  // k przynajmniej 2 i co najwyżej 8 oraz <= n
  const kMin = 2, kMax = Math.min(8, n);
  const k = Math.floor(Math.random() * (kMax - kMin + 1)) + kMin;

  // suma po i = 1..k-1: C(w, i) * C(m, k-i)
  let answer = 0;
  for (let i = 1; i <= k - 1; i++) answer += nCk(w, i) * nCk(m, k - i);

  return { type: "CombinationTeamAtLeastOneFromEachTask", data: { w, m, n, k, answer } };
}

export function renderTask({ data }) {
  const { w, m, k } = data;
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        W grupie jest <InlineMath math={`${w}`} /> kobiet i{" "}
        <InlineMath math={`${m}`} /> mężczyzn.
      </p>
      <p>
        Na ile sposobów można wybrać <InlineMath math={`${k}`} />-osobową drużynę,
        jeśli w składzie ma być <strong>co najmniej jedna kobieta i co najmniej jeden mężczyzna</strong>?
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
      placeholder="Wpisz liczbę sposobów"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object")
    return { correct:false, message:"Podaj liczbę." };
  const given = parseInt(userAnswer.raw, 10);
  if (Number.isNaN(given)) return { correct:false, message:"Podaj liczbę całkowitą." };
  const correct = given === data.answer;
  userAnswer.correctAnswer = data.answer;
  return { correct, message: correct ? "Dobrze!" : "Spróbuj ponownie." };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.answer}`} />;
}

export function renderUserAnswer(userAnswer) {
  return (!userAnswer || !userAnswer.raw)
    ? <i>(brak odpowiedzi)</i>
    : <InlineMath math={`${userAnswer.raw}`} />;
}

export function renderExplanation({ data }) {
  const { w, m, n, k, answer } = data;

  // --- SPOSÓB A: przygotuj wiersze i wartości ---
  const perI = [];
  for (let i = 1; i <= k - 1; i++) {
    const cw = nCk(w, i);
    const cm = nCk(m, k - i);
    const term = cw * cm;
    perI.push({ i, cw, cm, term });
  }
  const hasMany = perI.length >= 2;
  const termVals = perI.map(r => r.term);
  const sumValsLatex = hasMany
    ? termVals.join(' + ') + ' = ' + answer
    : `${perI[0]?.cw}\\cdot ${perI[0]?.cm}=${answer}`;

  // --- SPOSÓB B: dopełnienie do całości ---
  const totalAll = nCk(n, k);
  const onlyWomen = nCk(w, k);
  const onlyMen = nCk(m, k);

  const onlyWomenNote = (onlyWomen === 0 && k > w) ? ' (0 — za mało kobiet, k > w)' : '';
  const onlyMenNote   = (onlyMen   === 0 && k > m) ? ' (0 — za mało mężczyzn, k > m)' : '';

  const equalityLatex =
    `\\sum_{i=1}^{${k - 1}} \\binom{${w}}{i}\\,\\binom{${m}}{${k}-i} \\,=\\, ` +
    `\\binom{${n}}{${k}} - \\binom{${w}}{${k}} - \\binom{${m}}{${k}}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      {/* SPOSÓB A */}
      <p><strong>Sposób A. Rozkład według liczby kobiet.</strong></p>
      <p>
        Niech <InlineMath math={'i'} /> oznacza liczbę kobiet w drużynie.
        Warunek „co najmniej 1 kobieta i co najmniej 1 mężczyzna” daje
        <InlineMath math={`i\\in\\{1,2,\\dots,${k-1}\\}`} />.
        Dla ustalonego <InlineMath math={'i'} /> wybieramy kobiety na
        <InlineMath math={`\\binom{${w}}{i}`} /> sposobów oraz mężczyzn na
        <InlineMath math={`\\binom{${m}}{${k}-i}`} /> sposobów (iloczyn).
      </p>

      <p><strong>Krok A1. Policzenie każdego dwumianu i iloczynu dla kolejnych <InlineMath math={'i'} />.</strong></p>
      <div style={tableWrap}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>i</th>
              <th style={th}>Wybór kobiet</th>
              <th style={th}>Wybór mężczyzn</th>
              <th style={th}>Iloczyn</th>
            </tr>
          </thead>
          <tbody>
            {perI.map(({ i, cw, cm, term }) => {
              const womenLatex = `\\binom{${w}}{${i}}=${cw}`;
              const menLatex   = `\\binom{${m}}{${k - i}}=${cm}`;
              const prodLatex  = `${cw}\\cdot ${cm}=${term}`;
              const womenZeroNote = (cw === 0 && i > w) ? ' (0 — za mało kobiet)' : '';
              const menZeroNote   = (cm === 0 && (k - i) > m) ? ' (0 — za mało mężczyzn)' : '';
              return (
                <tr key={i}>
                  <td style={td}><InlineMath math={`${i}`} /></td>
                  <td style={td}>
                    <InlineMath math={womenLatex} />
                    {womenZeroNote && <span style={noteStyle}>{womenZeroNote}</span>}
                  </td>
                  <td style={td}>
                    <InlineMath math={menLatex} />
                    {menZeroNote && <span style={noteStyle}>{menZeroNote}</span>}
                  </td>
                  <td style={td}><InlineMath math={prodLatex} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p>
        <strong>
          Krok A2. {hasMany ? 'Zsumowanie iloczynów' : 'Wynik dla jedynego przypadku'}
          .
        </strong>
      </p>
      <BlockMath math={sumValsLatex} />

      {/* SPOSÓB B */}
      <p style={{ marginTop: 16 }}><strong>Sposób B. Dopełnienie do całości.</strong></p>

      <p><strong>Krok B1. Obliczenie liczby wszystkich <InlineMath math={`${k}`} />-osobowych zespołów.</strong></p>
      <p>
        Cała grupa ma <InlineMath math={`${n}=${w}+${m}`} /> osób.
      </p>
      <BlockMath math={`\\binom{${n}}{${k}}=${totalAll}`} />

      <p><strong>Krok B2. Wyznaczenie przypadków niedozwolonych.</strong></p>
      <ul style={{ marginTop: 8 }}>
        <li>
          tylko kobiety: <InlineMath math={`\\binom{${w}}{${k}}=${onlyWomen}`} />
          {onlyWomenNote && <span style={noteStyle}>{onlyWomenNote}</span>}
        </li>
        <li>
          tylko mężczyźni: <InlineMath math={`\\binom{${m}}{${k}}=${onlyMen}`} />
          {onlyMenNote && <span style={noteStyle}>{onlyMenNote}</span>}
        </li>
      </ul>

      <p><strong>Krok B3. Odjęcie skrajnych przypadków.</strong></p>
      <BlockMath math={`${totalAll}-${onlyWomen}-${onlyMen}=${answer}`} />

      <p style={{ marginTop: 12 }}>
        <strong>Obie metody dają ten sam wynik.</strong>
      </p>
      <BlockMath math={equalityLatex} />

      <p><strong>Wynik:</strong></p>
      <BlockMath math={`${answer}`} />
    </div>
  );
}

// style tabeli dopasowane do motywu (czarne w jasnym, białe w ciemnym)
const tableWrap = { overflowX: 'auto' };
const tableStyle = { borderCollapse: 'collapse', minWidth: 520, color: 'inherit' };
const baseCell = {
  border: '1px solid currentColor',
  padding: '6px 8px',
  textAlign: 'center'
};
const th = { ...baseCell, fontWeight: 600, background: 'transparent' };
const td = { ...baseCell };
const noteStyle = { opacity: 0.7, marginLeft: 6, fontSize: 12, color: 'inherit' };
